'use client';

import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import Link from 'next/link';
import { useSession, signIn } from 'next-auth/react';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import {
  useI4Links,
  useI4RedirectUrl,
  useI4LinkUrl,
} from '@/publicSearch/graphql/i4/hooks';
import type { UserI4LinkDto } from '@/publicSearch/graphql/i4/types';
import { Typography } from '@/primitives';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';

/**
 * Rendered when the user selects the Online Registrations tab.
 * - Unauthenticated: prompt to sign in.
 * - Linked: open button only (tab click in SearchTabs opens the popup directly).
 * - Not linked: link account or continue as guest.
 */
export default function OnlineRegistrationsTab() {
  const society = usePublicSearchContext((c) => c.society);
  const { status } = useSession();
  const pathname = usePathname();
  const router = useRouter();

  const societyCode = society.societyCode ?? '';
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;

  const onlineRegSetting = society.settings?.find(
    (s) =>
      s.societySettingType === SOCIETY_SETTING_TYPE.ONLINE_REGISTRATIONS_TAB,
  );
  const isILROnline =
    onlineRegSetting?.onlineRegistrationProvider?.trim()?.toUpperCase() ===
    'ILRONLINE';
  const accountLabel = isILROnline ? 'ILROnline' : 'Internet Solutions';

  const { data: linksData, loading: linksLoading } = useI4Links(
    !isAuthenticated || !societyCode,
  );
  const links = linksData?.i4Links ?? [];
  const linkForSociety = links.find(
    (l: UserI4LinkDto) => l.societyCode === societyCode,
  );
  const isLinked = !!linkForSociety;

  const {
    data: redirectUrlData,
    loading: redirectLoading,
    error: redirectError,
    refetch: refetchRedirectUrl,
  } = useI4RedirectUrl(
    {
      societyCode,
      runMode: 'IO',
      societyId: linkForSociety?.societyId,
    },
    !isAuthenticated || !isLinked,
  );

  // Prefetch link URL when not linked so "Start" on link-i4 page is instant
  const linkCallbackPath = isILROnline
    ? '/account/linked-accounts/ilronline-callback'
    : '/account/linked-accounts/i4-callback';
  const linkCallbackUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${linkCallbackPath}`
      : '';
  useI4LinkUrl(
    { societyCode, callbackUrl: linkCallbackUrl },
    !isAuthenticated || !societyCode || isLinked || !linkCallbackUrl,
  );

  // Redirect unauthenticated users to login with callback back to this tab.
  // Defer signIn so SessionProvider updates don't run during this component's mount commit.
  useEffect(() => {
    if (status !== SESSION_STATUS.UNAUTHENTICATED || !societyCode) return;
    const id = window.setTimeout(() => {
      const orgCode = pathname.split('/')[1] || society.societyCode || '';
      const callbackUrl = `/${orgCode}/${ROUTE_SEGMENT.EXTERNAL}/${ROUTE_SEGMENT.REGISTRATIONS}`;
      void signIn(AUTH_PROVIDER, { callbackUrl });
    }, 0);
    return () => clearTimeout(id);
  }, [status, pathname, society.societyCode, societyCode]);

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          p: 3,
        }}
      >
        <Alert severity="info">
          Please sign in to access Online Registrations.
        </Alert>
      </Box>
    );
  }

  // While resolving link status from i4Links, show loading to avoid flashing "Link account" when already linked
  if (linksLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 4,
          gap: 2,
        }}
      >
        <CircularProgress size={28} />
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  const orgCode = pathname.split('/')[1] || society.societyCode || '';
  const linkLandingPath = `/${orgCode}/${ROUTE_SEGMENT.EXTERNAL}/link-i4`;

  // Not linked: show CTA to link account
  if (!isLinked) {
    return (
      <Box
        sx={{
          p: 3,
        }}
      >
        <Stack
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography sx={{ textAlign: 'center' }}>
            Link your {accountLabel} account to access Online Registrations from
            BreedLOGIC.
          </Typography>
          <Button
            variant="contained"
            onClick={() => router.push(linkLandingPath)}
          >
            Link my {accountLabel} account
          </Button>
        </Stack>
      </Box>
    );
  }

  const i4Url = redirectUrlData?.i4RedirectUrl;

  // Linked: show open button (e.g. when landing on this tab via URL). Tab click opens popup from SearchTabs.
  if (isLinked) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 4,
          gap: 2,
        }}
      >
        {(() => {
          if (redirectLoading) {
            return (
              <>
                <CircularProgress size={28} />
                <Typography
                  sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                >
                  Loading...
                </Typography>
              </>
            );
          }
          if (!i4Url) {
            return (
              <>
                {redirectError && (
                  <Alert severity="warning" sx={{ alignSelf: 'stretch' }}>
                    {redirectError.message ??
                      'Could not load the link. Please try again.'}
                    {' Go to your '}
                    <MuiLink
                      component={Link}
                      href="/account#linked-accounts"
                      sx={{ fontWeight: 600 }}
                    >
                      Linked accounts
                    </MuiLink>
                    {' to unlink, then link again.'}
                  </Alert>
                )}
                <Typography
                  sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                >
                  Open Online Registrations in a new tab.
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    disabled={!i4Url}
                    onClick={() =>
                      i4Url &&
                      window.open(i4Url, '_blank', 'noopener,noreferrer')
                    }
                  >
                    Open Online Registrations
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => refetchRedirectUrl()}
                    disabled={redirectLoading}
                  >
                    Try again
                  </Button>
                </Stack>
              </>
            );
          }
          return (
            <>
              <Typography
                sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
              >
                Open Online Registrations in a new tab.
              </Typography>
              <Button
                variant="contained"
                onClick={() =>
                  i4Url && window.open(i4Url, '_blank', 'noopener,noreferrer')
                }
              >
                Open Online Registrations
              </Button>
            </>
          );
        })()}
      </Box>
    );
  }

  return null;
}
